import React from 'react';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SwipeableListItem } from '../../../../../generic/swipe/SwipeableListAdapter';
import { SwipeAction } from '../../../../../generic/ui/SwipeAction';
import { CardRow } from '../../../../../generic/ui/CardRow';
import { Mono } from '../../../../../generic/ui/Mono';
import { Tag } from '../../../../../generic/ui/Tag/Tag';
import BarcodeIcon from '../../../../../generic/icons/IconLib/BarcodeIcon';
import UserIcon from '../../../../../generic/icons/IconLib/UserIcon';
import CoinIcon from '../../../../../generic/icons/IconLib/CoinIcon';
import PencilIcon from '../../../../../generic/icons/IconLib/PencilIcon';
import EditIcon from '../../../../../generic/icons/IconLib/PencilIcon';
import PhoneIcon from '../../../../../generic/icons/IconLib/PhoneIcon';
import PrintIcon from '../../../../../generic/icons/IconLib/PrintIcon';
import StampIcon from '../../../../../generic/icons/IconLib/StampIcon';
import { convertToDate } from '../../../../../modules/convertDateString';
import { LINKS } from '../../../../../Routs/config';
import { SELECT_TEUDA_TITLE } from '../../../../../../store/actions/actionsTypes';
import { ITeudotItem } from '../../../../../../models/RequestsModel/teudot';
import { ITeudotSmsData } from '../../../../../modules/API/Certificates/models/smsData';
import { TreqSign } from '../types/list';

export interface TeudaRowProps {
    item: ITeudotItem;
    isLast?: boolean;
    isMashgiah: boolean;
    isAllowToPrint?: boolean;
    isAllowToSendSms: boolean;
    isDisabledHandleTeudot?: boolean;
    print: (code: number, sub: number) => void;
    sign: (items: TreqSign[], mashgiah?: number) => void;
    sendSms: (data: ITeudotSmsData) => void;
    handleOpenPalletLogModal?: (item: ITeudotItem) => void;
}

const TeudaRow: React.FC<TeudaRowProps> = ({
    item,
    isLast = false,
    isMashgiah,
    isAllowToPrint = true,
    isAllowToSendSms,
    isDisabledHandleTeudot = false,
    print,
    sign,
    sendSms,
}) => {
    const [t] = useTranslation('teudot');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        GroupDesc,
        Delivery_Date,
        Delivery_Num,
        Delivery_Sub,
        Customer_Name,
        SellerDesc,
        Mashgiah,
    } = item;

    const formattedDate = convertToDate(Delivery_Date);
    const trimmedSeller = SellerDesc?.trim();
    const trimmedGroup = GroupDesc?.trim();
    const isTriim = trimmedGroup === 'טריים';

    const handlePrint = (e: React.MouseEvent) => {
        e.stopPropagation();
        print(Delivery_Num, Delivery_Sub);
    };
    const handleSendSms = (e: React.MouseEvent) => {
        if (!isAllowToSendSms) return;
        e.stopPropagation();
        sendSms({ Delivery_Num, Delivery_Sub });
    };
    const handleSign = (e: React.MouseEvent, mashgiah?: number) => {
        e.stopPropagation();
        sign([{ Delivery_Num, Delivery_Sub }], mashgiah);
    };
    const handleEdit = () => {
        if (isDisabledHandleTeudot) return;
        dispatch({ type: SELECT_TEUDA_TITLE, payload: item });
        navigate(
            `${LINKS.teudot.teudotMishloah.createByID}${Delivery_Num}/${Delivery_Sub}`,
        );
    };

    const stampSlot = isMashgiah ? (
        Mashgiah === 0 ? (
            <ActionBtn
                type='button'
                onClick={(e) => handleSign(e)}
                aria-label='sign'
            >
                <EditIcon />
            </ActionBtn>
        ) : (
            <ActionBtn
                type='button'
                onClick={(e) => handleSign(e, Mashgiah)}
                aria-label='unsign'
                $stamped
            >
                <StampIcon />
            </ActionBtn>
        )
    ) : Mashgiah !== 0 ? (
        <ActionBtn type='button' $stamped $disabled disabled aria-label='stamped'>
            <StampIcon />
        </ActionBtn>
    ) : null;

    return (
        <Wrapper>
            <SwipeableListItem
                swipeRight={{
                    content: (
                        <SwipeAction
                            variant='edit'
                            align='start'
                            icon={<PencilIcon />}
                            label={t('teudot_main_element_swipe_edit')}
                        />
                    ),
                    action: handleEdit,
                }}
                threshold={0.3}
                blockSwipe={!!isDisabledHandleTeudot}
            >
                <CardRow
                    $columns='6.5rem minmax(0, 1fr) auto'
                    $mobileColumns='5rem minmax(0, 1fr) auto'
                    $isLast={isLast}
                >
                    <CardRow.Cell>
                        <CardRow.Stack $gap='0.125rem'>
                            <CardRow.Line $gap='0.375rem'>
                                <IconSlot>
                                    <BarcodeIcon color='currentColor' size={16} />
                                </IconSlot>
                                <Id>{Delivery_Num}</Id>
                            </CardRow.Line>
                            <When>{formattedDate}</When>
                        </CardRow.Stack>
                    </CardRow.Cell>

                    <CardRow.Cell>
                        <CardRow.Stack $gap='0.25rem'>
                            <CardRow.Line $gap='0.5rem'>
                                <NameIcon>
                                    <UserIcon color='currentColor' size={16} />
                                </NameIcon>
                                <PrimaryName>{Customer_Name?.trim()}</PrimaryName>
                            </CardRow.Line>
                            {(trimmedSeller || trimmedGroup) && (
                                <CardRow.Line $gap='0.5rem' $wrap>
                                    {trimmedSeller && (
                                        <SellerLine>
                                            <SellerIcon>
                                                <CoinIcon color='currentColor' size={14} />
                                            </SellerIcon>
                                            <SellerText>{trimmedSeller}</SellerText>
                                        </SellerLine>
                                    )}
                                    {trimmedGroup && (
                                        <Tag
                                            variant='tinted'
                                            color={isTriim ? '#22C55E' : '#F59E0B'}
                                        >
                                            {trimmedGroup}
                                        </Tag>
                                    )}
                                </CardRow.Line>
                            )}
                        </CardRow.Stack>
                    </CardRow.Cell>

                    <CardRow.Cell $align='end'>
                        <Actions>
                            {isAllowToPrint && (
                                <ActionBtn
                                    type='button'
                                    onClick={handlePrint}
                                    aria-label='print'
                                >
                                    <PrintIcon />
                                </ActionBtn>
                            )}
                            <ActionBtn
                                type='button'
                                onClick={handleSendSms}
                                aria-label='sms'
                                $disabled={!isAllowToSendSms}
                                disabled={!isAllowToSendSms}
                            >
                                <PhoneIcon />
                            </ActionBtn>
                            {stampSlot}
                        </Actions>
                    </CardRow.Cell>
                </CardRow>
            </SwipeableListItem>
        </Wrapper>
    );
};

export default TeudaRow;

const Wrapper = styled.div`
    width: 100%;
`;

const IconSlot = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink2};
    flex-shrink: 0;
`;

const NameIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`;

const Id = styled(Mono)`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const When = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink4};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const PrimaryName = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const SellerLine = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 0;
`;

const SellerIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`;

const SellerText = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const Actions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
`;

const ActionBtn = styled.button<{ $disabled?: boolean; $stamped?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 2rem;
    min-width: 2rem;
    padding: 0 0.5rem;
    border-radius: 0.5rem;
    background: ${({ theme, $stamped }) =>
        $stamped ? theme.colors.accentSoft : theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    border: 1px solid
        ${({ theme, $stamped }) =>
            $stamped ? theme.colors.accentSoft : theme.colors.accent};
    cursor: pointer;
    user-select: none;
    transition: all ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1rem;
        height: 1rem;
    }

    svg {
        width: 0.875rem; height: 0.875rem;
        line-height: 1;
    }

    &:hover:not(:disabled) {
        background: ${({ theme }) => theme.colors.accentDeep};
        border-color: ${({ theme }) => theme.colors.accentDeep};
    }

    &:active:not(:disabled) {
        transform: translateY(1px);
    }

    ${({ $disabled }) =>
        $disabled &&
        css`
            opacity: 0.45;
            cursor: not-allowed;
        `}
`;
